import moment from 'moment';
import useApi, { ApiMethod } from '~/hooks/useApi';
import endpoints from '~/services/endpoints';
import { formatScreenLabel, getAuditScreens } from '~/utils/auditCorrelation';
import {
  ActivityQuery,
  ActivityResource,
  ActivityResources,
  AUDIT_ALL,
  AuditChartGranularity,
  AuditChartMetric,
  AuditChartPoint,
  AuditFilters,
  AuditOption,
  UsuarioResumoResponse,
} from './models';

const ISO = 'YYYY-MM-DDTHH:mm:ss';
const BASE_URL = endpoints.consult.getAccessControlActivity;

// Limites usados por card (o contrato define o máximo de cada endpoint).
export const ACTIVITY_LIMITS = {
  telas: 20,
  servicos: 30,
  endpoints: 30,
  usuarios: 50,
  visitas: 100,
};

// Acima disso o back recusa agrupamento por hora (máximo de 1000 pontos).
export const MAX_HOURLY_POINTS = 1000;

const parseDate = (date: string) => moment(date, 'DD/MM/YYYY', true);

const optional = (value: string) =>
  value && value !== AUDIT_ALL ? value : undefined;

// dataInicio é inclusiva e dataFim exclusiva: o dia final inteiro entra
// enviando a meia-noite do dia seguinte.
export const toActivityQuery = (filters: AuditFilters): ActivityQuery => {
  const inicio = parseDate(filters.dataInicial);
  const fim = parseDate(filters.dataFinal);
  return {
    dataInicio: inicio.isValid() ? inicio.startOf('day').format(ISO) : undefined,
    dataFim: fim.isValid() ? fim.add(1, 'day').startOf('day').format(ISO) : undefined,
    correlationRoute: optional(filters.tela),
    service: optional(filters.servico),
    userId: optional(filters.usuario),
    marcador: optional(filters.marcador),
  };
};

export const hourlyPoints = (filters: AuditFilters): number => {
  const inicio = parseDate(filters.dataInicial);
  const fim = parseDate(filters.dataFinal);
  if (!inicio.isValid() || !fim.isValid()) return 0;
  return fim.add(1, 'day').diff(inicio, 'hours');
};

const useResourceApi = (resource: ActivityResource) =>
  useApi(`${BASE_URL}/${resource}`, ApiMethod.GET, {}, true);

export const useAccessControlActivity = () => {
  // Um useApi por recurso; a ordem das chamadas é fixa.
  const apis: Record<ActivityResource, ReturnType<typeof useResourceApi>> = {
    'visao-geral': useResourceApi('visao-geral'),
    'serie-temporal': useResourceApi('serie-temporal'),
    telas: useResourceApi('telas'),
    servicos: useResourceApi('servicos'),
    marcadores: useResourceApi('marcadores'),
    endpoints: useResourceApi('endpoints'),
    usuarios: useResourceApi('usuarios'),
    visitas: useResourceApi('visitas'),
  };

  // callApi do useApi muda a cada render; não usar fetchResource em deps de efeito.
  const fetchResource = async <R extends ActivityResource>(
    resource: R,
    query: ActivityQuery,
    params: ActivityResources[R]['params'] = {},
  ): Promise<ActivityResources[R]['response']> => {
    let response;
    try {
      response = await apis[resource].callApi({ ...query, ...params });
    } catch {
      throw new Error('Não foi possível consultar as atividades. Tente novamente.');
    }
    if (!response.success) {
      // Erros do ms-access-control vêm em { message }.
      const body = response.data as { message?: string } | null;
      throw new Error(
        body?.message || response.mensagem || 'Não foi possível consultar as atividades.',
      );
    }
    return response.data as ActivityResources[R]['response'];
  };

  return { fetchResource };
};

// Usuários do backoffice no ms-usuario (todos os perfis, exceto USUARIO; até
// 50, ordenados por nome). Sem nome, traz os primeiros 50. O id da opção é o
// user._id, o mesmo valor do filtro userId das atividades.
export const useUserSearch = () => {
  const api = useApi<UsuarioResumoResponse[]>(
    endpoints.consult.getBackofficeUsers,
    ApiMethod.GET,
    {},
    true,
  );

  const searchUsers = async (nome = ''): Promise<AuditOption[]> => {
    const response = await api.callApi({ nome });
    if (!response.success) throw new Error('Erro ao buscar usuários');
    return (response.data ?? [])
      .filter((user) => user.id && user.nome)
      .map((user) => ({ id: user.id, value: user.nome }));
  };

  return { searchUsers };
};

// ---------------------------------------------------------------------------
// Formatação compartilhada pelos cards
// ---------------------------------------------------------------------------

// Há usuários gravados nas atividades sem nome; o id aparece logo abaixo.
export const userName = (nome?: string | null) =>
  nome?.trim() || 'Usuário sem nome';

export const formatDateTime =(iso: string | null) =>
  iso ? moment(iso, ISO).format('DD/MM/YYYY, HH:mm') : '-';

// periodoFim é exclusivo (00:00 do dia seguinte); mostra o último minuto incluído.
export const formatPeriodEnd = (iso: string | null) =>
  iso ? moment(iso, ISO).subtract(1, 'minute').format('DD/MM/YYYY, HH:mm') : '-';

export const screenLabel = (route: string): string => {
  const screen = getAuditScreens().find((item) => item.route === route);
  return screen ? formatScreenLabel(screen) : route;
};

export const toChartPoints = (
  pontos: ActivityResources['serie-temporal']['response']['pontos'],
  metric: AuditChartMetric,
  granularity: AuditChartGranularity,
): AuditChartPoint[] => {
  const labelFormat = granularity === 'HORA' ? 'DD/MM HH:mm' : 'DD/MM';
  return pontos.map((point) => ({
    label: moment(point.instante, ISO).format(labelFormat),
    valor: metric === 'VISITAS' ? point.visitas : point.chamadas,
  }));
};
